import {
  ErrorResponse,
  OtpResponse,
  RegistrationResponse,
  ServiceResponse,
  ValidateOtpResponse,
} from '@bzbs/react-api-client';
import React, { createContext, useContext, useEffect, useRef, useState } from 'react';
import { useBuzzebeesServiceContext } from './buzzebees_service_provider';
import { useAnalyticsContext } from './analytics_provider';

type RegisterContextType = {
  username: string;
  password: string;
  confirmPassword: string;
  contactNumber: string;
  firstName: string;
  lastName: string;
  otp: string;
  refCode: string;
  address: string | undefined;
  gender: string | undefined;
  birthDate: number | undefined;
  email: string | undefined;
  refUserCode: string | undefined;
  consentVersion: {
    termAndCondition: string;
    dataPrivacy: string;
    marketingOptions: string;
  } | null;
  termAndConditionAccepted: boolean;
  dataPrivacyAccepted: boolean;
  marketingAccepted: boolean;
  emailMarketingAccepted: boolean;
  smsMarketingAccepted: boolean;
  notificationMarketingAccepted: boolean;
  lineMarketingAccepted: boolean;
  phoneMarketingAccepted: boolean;
  isLoading: boolean;
  error: ErrorResponse | null;
  actions: {
    setUUID(value: string): void;
    setAppId(value: string): void;
    setDeviceToken(value: string): void;
    setClientVersion(value: string): void;
    setUsername(value: string): void;
    setPassword(value: string): void;
    setConfirmPassword(value: string): void;
    setContactNumber(value: string): void;
    setFirstName(value: string): void;
    setLastName(value: string): void;
    setOtp(value: string): void;
    setRefCode(value: string): void;
    setAddress(value: string): void;
    setGender(value: string): void;
    setBirthDate(value: number): void;
    setEmail(value: string): void;
    setRefUserCode(value: string): void;
    setTermAndConditionAccepted(value: boolean): void;
    setDataPrivacyAccepted(value: boolean): void;
    setMarketingAccepted(value: boolean): void;
    setEmailMarketingAccepted(value: boolean): void;
    setSmsMarketingAccepted(value: boolean): void;
    setNotificationMarketingAccepted(value: boolean): void;
    setLineMarketingAccepted(value: boolean): void;
    setPhoneMarketingAccepted(value: boolean): void;
    setError(value: ErrorResponse | null): void;
    setLatestConsentVersion(termAndCondition: string, dataPrivacy: string, marketingOptions: string): void;
    clearData: () => void;
    sendOtp: () => Promise<ServiceResponse<OtpResponse>>;
    validateOtp: () => Promise<ServiceResponse<ValidateOtpResponse>>;
    register: () => Promise<ServiceResponse<RegistrationResponse>>;
  };
};

const RegisterContext = createContext({
  username: '',
  password: '',
  confirmPassword: '',
  contactNumber: '',
  firstName: '',
  lastName: '',
  otp: '',
  refCode: '',
  address: undefined,
  gender: undefined,
  birthDate: undefined,
  email: undefined,
  refUserCode: undefined,
  consentVersion: {
    termAndCondition: '',
    dataPrivacy: '',
    marketingOptions: '',
  },
  termAndConditionAccepted: false,
  dataPrivacyAccepted: false,
  marketingAccepted: false,
  emailMarketingAccepted: false,
  smsMarketingAccepted: false,
  notificationMarketingAccepted: false,
  lineMarketingAccepted: false,
  phoneMarketingAccepted: false,
  isLoading: false,
  error: null,
  actions: {
    setUUID: () => {},
    setAppId: () => {},
    setDeviceToken: () => {},
    setClientVersion: () => {},
    setUsername: () => {},
    setPassword: () => {},
    setConfirmPassword: () => {},
    setContactNumber: () => {},
    setFirstName: () => {},
    setLastName: () => {},
    setOtp: () => {},
    setRefCode: () => {},
    setAddress: () => {},
    setGender: () => {},
    setBirthDate: () => {},
    setEmail: () => {},
    setRefUserCode: () => {},
    setTermAndConditionAccepted: () => {},
    setDataPrivacyAccepted: () => {},
    setMarketingAccepted: () => {},
    setEmailMarketingAccepted: () => {},
    setSmsMarketingAccepted: () => {},
    setNotificationMarketingAccepted: () => {},
    setLineMarketingAccepted: () => {},
    setPhoneMarketingAccepted: () => {},
    setError: () => {},
    setLatestConsentVersion: () => {},
    clearData: () => {},
    sendOtp: async () => ({ type: 'client-error', message: 'Not implemented' }),
    validateOtp: async () => ({ type: 'client-error', message: 'Not implemented' }),
    register: async () => ({ type: 'client-error', message: 'Not implemented' }),
  },
} as RegisterContextType);

export function useRegisterContext() {
  return useContext(RegisterContext);
}

export function RegisterProvider({ children }: { children: React.ReactNode }) {
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState<ErrorResponse | null>(null);

  const { bzbsService } = useBuzzebeesServiceContext();
  const { actions: analyticsActions } = useAnalyticsContext();

  const uuid = useRef<string>('');
  const appId = useRef<string>('');
  const deviceToken = useRef<string>('');
  const clientVersion = useRef<string>('');

  const [username, setUsername] = useState<string>('');
  const [password, setPassword] = useState<string>('');
  const [confirmPassword, setConfirmPassword] = useState<string>('');

  const [contactNumber, setContactNumber] = useState<string>('');
  const [firstName, setFirstName] = useState<string>('');
  const [lastName, setLastName] = useState<string>('');

  const [otp, setOtp] = useState<string>('');
  const [refCode, setRefCode] = useState<string>('');

  const [address, setAddress] = useState<string | undefined>();
  const [gender, setGender] = useState<string | undefined>();
  const [birthDate, setBirthDate] = useState<number | undefined>();
  const [email, setEmail] = useState<string | undefined>();
  const [refUserCode, setRefUserCode] = useState<string | undefined>();

  const [consentVersion, setConsentVersion] = useState<{
    termAndCondition: string;
    dataPrivacy: string;
    marketingOptions: string;
  } | null>(null);

  const [termAndConditionAccepted, setTermAndConditionAccepted] = useState<boolean>(false);
  const [dataPrivacyAccepted, setDataPrivacyAccepted] = useState<boolean>(false);
  const [marketingAccepted, setMarketingAccepted] = useState<boolean>(false);
  const [emailMarketingAccepted, setEmailMarketingAccepted] = useState<boolean>(false);
  const [smsMarketingAccepted, setSmsMarketingAccepted] = useState<boolean>(false);
  const [notificationMarketingAccepted, setNotificationMarketingAccepted] = useState<boolean>(false);
  const [lineMarketingAccepted, setLineMarketingAccepted] = useState<boolean>(false);
  const [phoneMarketingAccepted, setPhoneMarketingAccepted] = useState<boolean>(false);

  useEffect(() => {
    if (marketingAccepted === false) {
      setEmailMarketingAccepted(false);
      setSmsMarketingAccepted(false);
      setNotificationMarketingAccepted(false);
      setLineMarketingAccepted(false);
      setPhoneMarketingAccepted(false);
    }
  }, [marketingAccepted]);

  const setUUID = (value: string) => {
    uuid.current = value;
  };

  const setAppId = (value: string) => {
    appId.current = value;
  };

  const setDeviceToken = (value: string) => {
    deviceToken.current = value;
  };

  const setClientVersion = (value: string) => {
    clientVersion.current = value;
  };

  const setLatestConsentVersion = (termAndCondition: string, dataPrivacy: string, marketingOptions: string) => {
    setConsentVersion({
      termAndCondition: termAndCondition,
      dataPrivacy: dataPrivacy,
      marketingOptions: marketingOptions,
    });
  };

  const sendOtp = async (): Promise<ServiceResponse<OtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.otp({
      uuid: uuid.current,
      appId: appId.current,
      contactNumber: contactNumber.replace(/-/g, ''),
      channel: 'register',
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setRefCode(response.model.refcode ?? '');
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const validateOtp = async (): Promise<ServiceResponse<ValidateOtpResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.authApi.validateOtp({
      appId: appId.current,
      otp: otp,
      refCode: refCode,
      contactNumber: contactNumber.replace(/-/g, ''),
      use: false,
      channel: 'register',
    });
    setIsLoading(false);

    if (response.type === 'success') {
      setOtp(otp);
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const register = async (): Promise<ServiceResponse<RegistrationResponse>> => {
    setIsLoading(true);
    const response = await bzbsService.registerApi.register({
      appId: appId.current,
      uuid: uuid.current,
      macAddress: '',
      os: '',
      platform: '',
      clientVersion: clientVersion.current,
      deviceNotificationEnable: true,
      username: username,
      password: password,
      confirmPassword: confirmPassword,
      firstName: firstName,
      lastName: lastName,
      contactNumber: contactNumber.replace(/-/g, ''),
      otp: otp,
      refCode: refCode,
      address: address,
      gender: gender,
      birthdate: birthDate,
      email: email,
      refUserCode: refUserCode,
      termAndConditionVersion: consentVersion?.termAndCondition,
      dataPrivacyVersion: consentVersion?.dataPrivacy,
      marketingOptionsVersion: consentVersion?.marketingOptions,
      emailMarketing: emailMarketingAccepted ? '1' : '0',
      smsMarketing: smsMarketingAccepted ? '1' : '0',
      notificationMarketing: notificationMarketingAccepted ? '1' : '0',
      lineMarketing: lineMarketingAccepted ? '1' : '0',
      phoneMarketing: phoneMarketingAccepted ? '1' : '0',
      options: {},
    });
    setIsLoading(false);
    if (response.type === 'success') {
      analyticsActions.trackEvent({
        action: 'on_click',
        name: 'register_success',
        category: 'register',
      });
      setError(null);
    } else {
      setError(response);
    }

    return response;
  };

  const clearData = () => {
    setUsername('');
    setPassword('');
    setConfirmPassword('');
    setContactNumber('');
    setFirstName('');
    setLastName('');
    setOtp('');
    setRefCode('');
    setAddress(undefined);
    setGender(undefined);
    setBirthDate(undefined);
    setEmail(undefined);
    setRefUserCode(undefined);
    setConsentVersion(null);
    setTermAndConditionAccepted(false);
    setDataPrivacyAccepted(false);
    setMarketingAccepted(false);
    setEmailMarketingAccepted(false);
    setSmsMarketingAccepted(false);
    setNotificationMarketingAccepted(false);
    setLineMarketingAccepted(false);
    setPhoneMarketingAccepted(false);
  };

  return (
    <RegisterContext.Provider
      value={{
        isLoading,
        error,
        username,
        password,
        confirmPassword,
        contactNumber,
        firstName,
        lastName,
        otp,
        refCode,
        address,
        gender,
        birthDate,
        email,
        refUserCode,
        consentVersion,
        termAndConditionAccepted,
        dataPrivacyAccepted,
        marketingAccepted,
        emailMarketingAccepted,
        smsMarketingAccepted,
        notificationMarketingAccepted,
        lineMarketingAccepted,
        phoneMarketingAccepted,
        actions: {
          setUUID,
          setAppId,
          setDeviceToken,
          setClientVersion,
          setUsername,
          setPassword,
          setConfirmPassword,
          setContactNumber,
          setFirstName,
          setLastName,
          setOtp,
          setRefCode,
          setAddress,
          setGender,
          setBirthDate,
          setEmail,
          setRefUserCode,
          setTermAndConditionAccepted,
          setDataPrivacyAccepted,
          setMarketingAccepted,
          setEmailMarketingAccepted,
          setSmsMarketingAccepted,
          setNotificationMarketingAccepted,
          setLineMarketingAccepted,
          setPhoneMarketingAccepted,
          setError,
          setLatestConsentVersion,
          clearData,
          sendOtp,
          validateOtp,
          register,
        },
      }}
    >
      {children}
    </RegisterContext.Provider>
  );
}
